import { Flags } from "@/feature-flags";
import { useFlag } from "@/hooks/use-flag";
import { useResourcePermission } from "@/hooks/use-resource-permission";
import { useOrganization } from "@/queries/organization-queries";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

export type FeatureAutomations = {
    isLoading: boolean;
    accessLevel: AutomationsAccessLevel;
    capabilities: Set<AutomationsCapabilities>;
};

export enum AutomationsAccessLevel {
    None = "none",
    Teaser = "teaser",
    Full = "full",
}

export enum AutomationsCapabilities {
    Webhooks = "webhooks",
    ServiceAccountExecutor = "service-account-executor",
}

export const useFeatureAutomations = (): FeatureAutomations => {
    const { data: organization, isPending: isPendingOrganization } = useOrganization();
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.WorkflowsEnabled, false);
    const { value: webhooksAllowed, loading: webhooksLoading } = useFlag(Flags.AutomationsWebhooksAllowed, false);

    // For enterprise orgs, check if the user has automations permissions via RBAC
    // (e.g. via org:automations_admin wildcard grants). This allows users with
    // RBAC access to use automations even when the feature flag is not enabled
    // for their org.
    const { hasPermission: hasAutomationsPermission, isLoading: isLoadingPermission } = useResourcePermission(
        ResourceType.ORGANIZATION,
        organization?.id ?? "",
        "workflow:create",
    );

    if (isPendingOrganization || flagLoading || webhooksLoading) {
        return {
            accessLevel: AutomationsAccessLevel.None,
            capabilities: new Set(),
            isLoading: true,
        };
    }

    // Access controlled by organization tier and organization configuration
    let feature: FeatureAutomations = {
        accessLevel: AutomationsAccessLevel.None,
        capabilities: new Set(),
        isLoading: false,
    };
    switch (organization?.tier) {
        case OrganizationTier.FREE: {
            feature = {
                accessLevel: AutomationsAccessLevel.None,
                capabilities: new Set(),
                isLoading: false,
            };
            break;
        }
        case OrganizationTier.FREE_ONA: {
            feature = {
                accessLevel: AutomationsAccessLevel.Teaser,
                capabilities: new Set(),
                isLoading: false,
            };
            break;
        }
        case OrganizationTier.CORE: {
            feature = {
                accessLevel: AutomationsAccessLevel.Full,
                capabilities: new Set(),
                isLoading: false,
            };
            break;
        }
        case OrganizationTier.ENTERPRISE: {
            // TODO(Enterprise rollout): Introduce logic for when to have it enabled by default for enterprise customers
            feature = {
                accessLevel: AutomationsAccessLevel.None,
                capabilities: new Set([
                    AutomationsCapabilities.Webhooks,
                    AutomationsCapabilities.ServiceAccountExecutor,
                ]),
                isLoading: false,
            };
            break;
        }
        default: {
            feature = {
                accessLevel: AutomationsAccessLevel.None,
                capabilities: new Set(),
                isLoading: false,
            };
            break;
        }
    }

    // Apply feature flag overwrites
    if (flagEnabled) {
        feature.accessLevel = AutomationsAccessLevel.Full;
    }
    if (webhooksAllowed) {
        feature.capabilities.add(AutomationsCapabilities.Webhooks);
    }

    // For enterprise orgs without the flag, grant access if the user has
    // automations RBAC permissions (e.g. via automations_admin role).
    if (organization?.tier === OrganizationTier.ENTERPRISE && feature.accessLevel === AutomationsAccessLevel.None) {
        if (hasAutomationsPermission) {
            feature.accessLevel = AutomationsAccessLevel.Full;
        }

        // Still loading RBAC permissions — avoid a flash of "no access"
        // before the permission check resolves.
        if (!hasAutomationsPermission && isLoadingPermission) {
            return {
                ...feature,
                isLoading: true,
            };
        }
    }

    return feature;
};
